import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Stack, Typography } from '@mui/material';

import type { CorridorCampaignRunSummary } from '~/types/corridorOpportunity';

import CampaignRunMetric from './CampaignRunMetric';

export default function CampaignRunSummary({ run }: { run: CorridorCampaignRunSummary | null | undefined }) {
  if (!run) {
    return (
      <Alert severity="warning">
        No completed daily campaign run is available yet. The queue cannot prove route coverage until a run completes.
      </Alert>
    );
  }

  const eligibleQualifiedCount = Math.max(0, run.qualifiedCount - run.duplicateCount);
  const durationMinutes = Math.ceil(run.durationMs / 60000);
  const runSeverity =
    run.status === 'FAILED'
      ? 'error'
      : run.status === 'COMPLETED' && run.coveragePct === 100 && run.draftSlaMet
        ? 'success'
        : 'warning';
  const runLabel =
    run.status === 'COMPLETED' && !run.draftSlaMet
      ? 'Completed · draft SLA missed'
      : run.status === 'COMPLETED_WITH_BLOCKERS'
        ? 'Completed with blockers'
        : run.status.toLowerCase();

  return (
    <Stack gap={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
        <Box>
          <Typography variant="h6">Daily campaign run</Typography>
          <Typography variant="body2" color="text.secondary">
            Last completed {new Date(run.completedAt).toLocaleString('en-AU')} · private drafts only
          </Typography>
        </Box>
        <Chip
          color={runSeverity}
          label={runLabel}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, textTransform: 'capitalize' }}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        }}
      >
        <CampaignRunMetric
          title="Universe processed"
          value={`${run.coveragePct}% coverage`}
          detail={`${run.processedCarrierDestinationCount} of ${run.carrierDestinationCount} carrier–destination pairs evaluated`}
        />
        <CampaignRunMetric
          title="Fare qualified"
          value={run.qualifiedCount.toLocaleString('en-AU')}
          detail={`${run.notQualifiedCount.toLocaleString('en-AU')} did not pass the LE price benchmark and every hard gate`}
        />
        <CampaignRunMetric
          title="Private drafts created"
          value={`${run.automationRatePct}% draft automation`}
          detail={`${run.draftReadyCount} of ${eligibleQualifiedCount} qualifying, non-duplicate opportunities became private drafts`}
        />
        <CampaignRunMetric
          title="15-minute draft SLA"
          value={`${run.draftSlaMet ? 'Target met' : 'Target missed'} · ${run.automationWithinTargetRatePct}%`}
          detail={`${run.draftReadyWithinTargetCount} of ${eligibleQualifiedCount} eligible qualifying drafts were persisted within 15 minutes; the full run took ${durationMinutes} min`}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        Draft automation excludes {run.duplicateCount} qualifying duplicate{run.duplicateCount === 1 ? '' : 's'} because
        the engine must not create overlapping drafts. Raw qualifying-to-draft rate: {run.rawQualifiedDraftRatePct}%.
      </Typography>

      {run.blockers.length > 0 && (
        <Stack gap={1}>
          <Alert severity="warning">
            <Typography fontWeight={700}>
              {run.blockedCount} blocked outcome{run.blockedCount === 1 ? '' : 's'} recorded
            </Typography>
            <Typography variant="body2">
              Every unsuccessful engine outcome has a stored reason. Expand the log to see source, supplier,
              duplicate-check, draft and persistence failures—including failures that could not produce a queue row.
            </Typography>
          </Alert>
          <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>View all {run.blockers.length} blocker messages</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack component="ol" gap={1} sx={{ m: 0, pl: 2.5 }}>
                {run.blockers.map((item, index) => (
                  <Typography component="li" variant="body2" key={`${item.code}-${index}`}>
                    <strong>{item.code.replace(/_/g, ' ')}</strong> · {item.message}
                  </Typography>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}
    </Stack>
  );
}
